import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { listFiltersService, ListTableSort, getSortedCollection } from "sprint-common-web";

import { mapAirlinesToDisplay } from "../../../../../common/utils/mapAirlinesToDisplay";
import { ContentVersionSource } from "../../../../common/types/ContentVersionSource";
import { mapToVersionSource } from "../../../../common/utils/mapToVersionSource";
import { MovieContext } from "../../context/MovieContext";
import { MovieVersionDetails } from "../../MovieDetails";
import { UpdatedVersionData } from "../edit-version-form/EditMovieVersionForm";
import { defaultMovieVersionListingFilters, MovieVersionListingFilters } from "../MovieVersionListingFilters";
import { MovieVersionListingItem } from "../MovieVersionListingItem";
import { MovieVersionListingContext } from "./MovieVersionListingContext";

type MovieVersionListingProviderProps = React.PropsWithChildren<unknown>;

export const MovieVersionListingProvider: React.FunctionComponent<MovieVersionListingProviderProps> = ({
    children
}: MovieVersionListingProviderProps) => {
    const location = useLocation();
    const history = useHistory();
    const filtersService = useMemo(
        () => listFiltersService<MovieVersionListingFilters>(defaultMovieVersionListingFilters),
        []
    );

    const [versionsToMerge, setVersionsToMerge] = useState<MovieVersionListingItem[]>([]);
    const [listingItems, setListingItems] = useState<MovieVersionListingItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [filters, setFilters] = useState<MovieVersionListingFilters>(() =>
        filtersService.getSavedFilters(location.search)
    );

    const {
        movie,
        isLoading,
        addMovieVersion,
        updateMovieVersion: updateMovieVersionInContext,
        setVersionAsMerged
    } = useContext(MovieContext);

    useEffect(() => {
        if (!isLoading && filters) {
            filterListingItems(filters);
        }
    }, [filters, isLoading]);

    const addNewVersionToMovie = (newMovieVersion: MovieVersionDetails) => {
        if (!movie) return;
        setListingItems((prevList: MovieVersionListingItem[]) => {
            if (prevList.length >= filters.pageSize) prevList.pop();
            const newListingItems = [
                ...mapToVersionItem([{ ...newMovieVersion, isModified: true }], false),
                ...prevList
            ];
            setTotalItems(newListingItems.length);

            return newListingItems;
        });

        addMovieVersion(newMovieVersion);
    };

    const updateMovieVersion = (updatedVersionData: UpdatedVersionData) => {
        updateMovieVersionInContext(
            updatedVersionData.versionId,
            updatedVersionData.distributor,
            updatedVersionData.type
        );

        const version = listingItems.find((item: MovieVersionListingItem) => item.id === updatedVersionData.versionId);
        if (version) {
            version.distributorName = updatedVersionData.distributor.name;
            version.typeName = updatedVersionData.type.name;
            version.isTrailer = updatedVersionData.isTrailer;
            version.isModified = true;
            version.source = mapToVersionSource(version);
        }

        setListingItems(listingItems);
    };

    const setMovieVersionAsMerged = (versionId: string, mergedTargetId: string) => {
        setVersionAsMerged(versionId, mergedTargetId);

        filterListingItems(filters);
    };

    const setSort = (sort: ListTableSort) => {
        saveAndSetFilters({
            sortBy: sort.by as keyof MovieVersionListingItem,
            sortOrder: sort.order,
            page: 1
        });
    };

    const setPage = (page: number) => {
        saveAndSetFilters({ page });
    };

    const setPageSize = (pageSize: number): void => {
        saveAndSetFilters({ pageSize, page: 1 });
    };

    const setSources = (sources?: ContentVersionSource[]): void => {
        saveAndSetFilters({ sources });
    };

    const setIncludeMerged = (includeMerged: boolean): void => {
        saveAndSetFilters({ includeMerged });
    };

    const saveAndSetFilters = (newFilters: Partial<MovieVersionListingFilters>): void => {
        setFilters((prevFilters: MovieVersionListingFilters) => {
            const updatedFilters = { ...prevFilters, ...newFilters };

            filtersService.saveFilters(location.pathname, history, updatedFilters);

            return updatedFilters;
        });
    };

    const clearFilters = (): void => {
        saveAndSetFilters(defaultMovieVersionListingFilters);
    };

    const filterListingItems = (filter: MovieVersionListingFilters) => {
        const filteredItems = getFilteredVersionItems(mapToVersionItem(movie.versions), filter);

        setTotalItems(filteredItems.length);

        const sortedItems = getSortedCollection(filteredItems, filter.sortBy, filter.sortOrder);
        const currentPageItems = getCurrentPageItems(sortedItems, filter);

        setListingItems(currentPageItems);
    };

    const addVersionToMergeList = (versionItem?: MovieVersionListingItem) => {
        if (!versionItem) return;

        setVersionsToMerge((prevVersions: MovieVersionListingItem[]) => {
            prevVersions.push(versionItem);

            return [...prevVersions];
        });
    };

    const removeVersionFromMergeList = (versionItem: MovieVersionListingItem) => {
        setVersionsToMerge((prevVersions: MovieVersionListingItem[]) => {
            const filteredVersionsItems = prevVersions.filter(
                (item: MovieVersionListingItem) => item.id !== versionItem.id
            );

            return [...filteredVersionsItems];
        });
    };

    const getCurrentPageItems = (
        items: MovieVersionListingItem[],
        filter: MovieVersionListingFilters
    ): MovieVersionListingItem[] => {
        const firstItem = (filter.page - 1) * filter.pageSize;
        const lastItem = filter.page * filter.pageSize;

        return items.slice(firstItem, lastItem);
    };

    const getFilteredVersionItems = (
        items: MovieVersionListingItem[],
        filter: MovieVersionListingFilters
    ): MovieVersionListingItem[] => {
        let filteredVersions = items;

        filteredVersions = filter.includeMerged
            ? filteredVersions
            : filteredVersions.filter((version: MovieVersionListingItem) => !version.merged === !filter.includeMerged);

        filteredVersions = filter.sources?.length
            ? filteredVersions.filter((version: MovieVersionListingItem) => filter.sources?.includes(version.source))
            : filteredVersions;

        return filteredVersions;
    };

    const clearVersionsToMerge = () => {
        setVersionsToMerge([]);
    };

    return (
        <MovieVersionListingContext.Provider
            value={{
                listingItems,
                isLoading,
                totalItems,
                updateMovieVersion,
                clearFilters,
                setSort,
                setPage,
                setPageSize,
                setIncludeMerged,
                setSources,
                addNewVersionToMovie,
                filters,
                movieDetails: movie,
                versionsToMerge,
                removeVersionFromMergeList,
                addVersionToMergeList,
                clearVersionsToMerge,
                setMovieVersionAsMerged
            }}
        >
            {children}
        </MovieVersionListingContext.Provider>
    );
};

const mapToVersionItem = (movieVersions: MovieVersionDetails[], resetNewFlag = true): MovieVersionListingItem[] => {
    return movieVersions.map((version: MovieVersionDetails) => {
        return {
            id: version.id,
            buzzVersion: version.buzzVersion,
            shreqVersion: version.shreqVersion,
            distributorName: version.distributor.name,
            typeName: version.type.name,
            typeId: version.type.id,
            merged: !!version.mergedTargetId,
            orphaned: !version.buzzVersion,
            isModified: resetNewFlag ? false : version.isModified,
            brandings: version.brandings ? mapAirlinesToDisplay(version.brandings) : "",
            isTrailer: version.isTrailer,
            source: mapToVersionSource(version)
        };
    });
};
